/**
 * The landing page: a leaderboard over the whole catalog.
 *
 * DESIGN
 * ------
 * Mobile-first, and deliberately quiet. Established list-UI patterns:
 *   - one screen, one job: rank → photo → name → one number
 *   - criteria are chips you toggle, not a form you fill in
 *   - selecting several criteria RANKS BY ALL OF THEM AT ONCE
 *   - almost no prose; detail lives behind the row, one tap away
 *
 * The combined ranking averages each product's position across every chosen
 * criterion (a Borda count), because the metrics have incompatible units and
 * averaging raw values would be meaningless.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, getLeaderboard, type ApiMeta, type LeaderboardPayload } from '@/api/client';
import type { Product } from '@/types/product';
import { computeMetrics } from '@/lib/nutrition/calculations';
import { analyse, sortOptionById } from '@/lib/nutrition/ranking';
import { useAppState } from '@/state/AppState';
import { LeaderboardRow } from '@/components/LeaderboardRow';
import { Notice } from '@/components/Panels';
import { CategoryPicker } from '@/components/CategoryPicker';
import { CriteriaPicker } from '@/components/CriteriaPicker';
import { CoverageBar } from '@/components/CoverageBar';
import { LoadingBar } from '@/components/LoadingBar';
import { checkCombination } from '@/lib/filter-sanity';
import { BOARDS } from '@/lib/boards';
import {
  readSnapshot,
  snapshotKey,
  writeSnapshot,
} from '@/lib/leaderboard-cache';
import { AdSlot } from '@/components/AdSlot';
import { fetchCoverage } from '@/lib/coverage-source';
import { InlineSearch } from '@/components/InlineSearch';

/** Rows per request. Big enough to fill a phone screen twice over. */
const PAGE_SIZE = 30;

type CombinedEntry = NonNullable<LeaderboardPayload['combined']>[number];

const AUDIENCES = [
  { id: 'human', label: 'People' },
  { id: 'pet', label: 'Pets' },
  { id: 'all', label: 'Both' },
] as const;

/**
 * Shown under protein-value rankings.
 *
 * "Grams of protein per dollar" answers how cheaply protein can be bought. It
 * does NOT rank protein quality: amino-acid profile and digestibility differ
 * between grains and animal sources, and we hold no data for either. Saying so
 * is better than inventing a quality-adjusted metric.
 */
const PROTEIN_CAVEAT =
  'Cheapest protein by weight — grains often win. Not a measure of protein quality.';

export function LeaderboardPage(): React.JSX.Element {
  const {
    toggleCompare, isComparing, compareFull,
    criteria: selected, setCriteria,
    leadCriterion, setLeadCriterion,
  } = useAppState();

  // The criterion whose number each row shows. Falls back to the first selected
  // so there is always exactly one, even before anything is starred.
  const lead = leadCriterion !== null && selected.includes(leadCriterion)
    ? leadCriterion
    : (selected[0] ?? 'cheapest_per_100g');

  const [category, setCategory] = useState<string | null>(null);
  const [audience, setAudience] = useState<'human' | 'pet' | 'all'>('human');
  const [critOpen, setCritOpen] = useState(false);
  /**
   * Bumped by the refresh button.
   *
   * Criteria and category changes already refetch on their own; this exists for
   * when a result set looks stale or a request failed, and it bypasses the
   * caches rather than replaying the response you are trying to get past.
   */
  const [refreshKey, setRefreshKey] = useState(0);
  const [coverage, setCoverage] = useState<number | null>(null);
  // Restored synchronously so the FIRST render already has rows, boards and
  // categories. Without this, coming back from a product page showed skeletons
  // and empty pickers — the filters looked cleared rather than merely loading.

  const initial = readSnapshot(snapshotKey(selected, category, audience));

  const [payload, setPayload] = useState<LeaderboardPayload | null>(initial?.payload ?? null);
  const [meta, setMeta] = useState<ApiMeta | null>(null);
  const [loading, setLoading] = useState(initial === null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Every row loaded so far, across pages.
   *
   * Held separately from `payload` because `payload` is always the LAST page:
   * the board list, category list and eligible count come from it, while the
   * rows accumulate.
   */
  /*
   * Coverage arrives with the ranking; no separate request.
   *
   * This used to be its own fetch on mount, doubling the API calls a landing
   * page makes. Under load the ceiling is the Workers free plan's 100,000
   * invocations a day -- shared across every function -- so a second request
   * for a single percentage halved how many visitors the site could serve.
   *
   * The fallback fetch stays for the case where an older cached payload has no
   * coveragePercent in it, so nothing breaks while caches turn over.
   */
  useEffect(() => {
    const fromPayload = payload?.coveragePercent;
    if (typeof fromPayload === 'number') {
      setCoverage(fromPayload);
      return;
    }
    if (payload === null) return;
    fetchCoverage()
      .then((c) => {
        if (c !== null) setCoverage(c.percent);
      })
      .catch(() => undefined);
  }, [payload]);

  const [products, setProducts] = useState<Product[]>(initial?.products ?? []);
  const [combined, setCombined] = useState<CombinedEntry[]>(initial?.combined ?? []);
  const [offset, setOffset] = useState(initial?.offset ?? 0);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinel = useRef<HTMLDivElement | null>(null);

  // Changing what is ranked invalidates every loaded row, so paging restarts.
  //
  // Skipped on the very first render: the rows there came from the snapshot and
  // match the current query, so clearing them would reintroduce the blank frame
  // this cache exists to remove.
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setProducts([]);
    setCombined([]);
    setOffset(0);
  }, [selected, category, audience, refreshKey]);

  useEffect(() => {
    const controller = new AbortController();
    if (offset === 0) setLoading(true);
    else setLoadingMore(true);
    setError(null);

    getLeaderboard(
      {
        board: selected[0],
        boards: selected,
        limit: PAGE_SIZE,
        category,
        audience,
        refresh: refreshKey,
        offset,
      },
      controller.signal,
    )
      .then((r) => {
        setPayload(r.data);
        setMeta(r.meta);
        // Append, de-duplicating on id: a row that shifted between requests
        // must not appear twice.
        setProducts((current) => {
          if (offset === 0) return r.data.products;
          const seen = new Set(current.map((p) => p.id));
          return [...current, ...r.data.products.filter((p) => !seen.has(p.id))];
        });
        setCombined((current) => {
          const next = r.data.combined ?? [];
          if (offset === 0) return next;
          const seen = new Set(current.map((c) => c.productId));
          return [...current, ...next.filter((c) => !seen.has(c.productId))];
        });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : 'Could not load rankings.');
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
        setLoadingMore(false);
      });

    return () => controller.abort();
  }, [selected, category, audience, refreshKey, offset]);

  // Save after every settled render so a later mount can restore this exact view.
  useEffect(() => {
    if (payload === null || products.length === 0) return;
    writeSnapshot({
      key: snapshotKey(selected, category, audience),
      payload,
      products,
      combined,
      offset,
    });
  }, [payload, products, combined, offset, selected, category, audience]);

  const eligible = payload?.eligible ?? 0;

  // Explains an empty or thin result rather than leaving the reader to toggle
  // filters at random until something reappears.
  const notice = checkCombination({
    selected,
    category,
    audience,
    eligible: payload === null ? null : eligible,
    returned: payload === null ? null : products.length,
    coveragePercent: coverage,
  });
  const hasMore = products.length > 0 && products.length < eligible;

  /**
   * Load the next page when the end of the list comes into view.
   *
   * `rootMargin` starts the request before the sentinel is actually visible, so
   * on a fast connection the next rows are already there by the time you reach
   * them.
   */
  useEffect(() => {
    const node = sentinel.current;
    if (node === null || !hasMore || loading || loadingMore) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setOffset((n) => n + PAGE_SIZE);
        }
      },
      { rootMargin: '600px 0px' },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, loading, loadingMore, products.length]);
  const isCombined = (payload?.selected?.length ?? 0) > 1;

  // Keep the server's order; the engine only supplies the per-product metrics.
  const rows = useMemo(() => {
    const analysed = analyse(products, computeMetrics, sortOptionById('price_per_100g'));
    const byId = new Map(analysed.map((r) => [r.product.id, r]));
    return products
      .map((p, i) => {
        const row = byId.get(p.id);
        // Position in the accumulated list, so rank keeps counting past page 1.
        return row ? { ...row, rank: i + 1 } : null;
      })
      .filter((r): r is NonNullable<typeof r> => r !== null);
  }, [products]);

  const toggle = (id: string): void => {
    setCriteria((cur) =>
      cur.includes(id)
        ? cur.length === 1
          ? cur // never leave zero criteria selected
          : cur.filter((x) => x !== id)
        : [...cur, id],
    );
  };

  const combinedFor = (productId: string) =>
    combined.find((c) => c.productId === productId) ?? null;

  const scoreFor = (productId: string): number | null => combinedFor(productId)?.score ?? null;

  return (
    <>
      {/* Criteria: tap to add, tap again to remove. Several = combined rank. */}
      <div className="pick">
        <div className="pick-bar">
          <CriteriaPicker
            options={(payload?.boards ?? BOARDS).map((b) => ({
              id: b.id,
              label: b.label,
              formula: b.formula,
              needsNutrition: b.needsNutrition,
              group: (b as { group?: 'value' | 'density' | 'amount' }).group ?? 'value',
            }))}
            selected={selected}
            onToggle={toggle}
            lead={lead}
            onLeadChange={setLeadCriterion}
            open={critOpen}
            onOpenChange={setCritOpen}
            eligible={payload?.eligible ?? null}
          />
        </div>

        <div className="pick-foot">
          <div className="audience" role="group" aria-label="Who for">
            {AUDIENCES.map((a) => (
              <button
                key={a.id}
                type="button"
                aria-pressed={audience === a.id}
                onClick={() => setAudience(a.id)}
              >
                {a.label}
              </button>
            ))}
          </div>
          <CategoryPicker
            categories={payload?.categories ?? []}
            selected={category}
            onChange={setCategory}
          />

          <button
            type="button"
            className="pick-refresh"
            onClick={() => setRefreshKey((n) => n + 1)}
            disabled={loading}
            aria-label="Refresh results"
            title="Refresh results"
          >
            <span className={loading ? 'spin' : undefined} aria-hidden="true">
              ↻
            </span>
          </button>

          <InlineSearch />
        </div>
      </div>

      {/* Any request in flight — first load, filter change, or paging. */}
      <LoadingBar active={loading || loadingMore} />

      {/* Beside the controls, where the eye already is — the top-edge bar
          alone is easy to miss on a tall phone. */}
      {(loading || loadingMore) && (
        <p className="load-pill" aria-live="polite">
          <span className="load-pill-dot" aria-hidden="true" />
          {loadingMore ? 'Loading more…' : 'Updating rankings…'}
        </p>
      )}

      <CoverageBar />

      {isCombined && (
        <p className="lead-note">
          Ordered by all {payload?.selected?.length} criteria · showing{' '}
          <strong>{BOARDS.find((b) => b.id === lead)?.label ?? lead}</strong>
          {' '}— tap ☆ in the criteria menu to change
        </p>
      )}

      {isCombined && (
        <p className="combined-note">
          Best across <strong>{payload?.selected?.length}</strong> criteria ·{' '}
          {payload?.eligible.toLocaleString()} qualify
          {meta?.static === true && (
            /*
             * Says what the combination actually covers.
             *
             * Combined here from the top of each chosen ranking, which is a
             * smaller set than a full catalogue sweep. That is a real rule and
             * worth stating: a product mediocre at every chosen criterion, and
             * so in the top of none, is not in this list.
             */
            <>
              {' · '}
              <span className="combined-scope">
                from the top of each ranking
              </span>
            </>
          )}
        </p>
      )}

      {notice !== null && (
        <p className={`combo-notice is-${notice.tone}`}>
          <span className="combo-notice-icon" aria-hidden="true">
            {notice.tone === 'conflict' ? '!' : 'i'}
          </span>
          <span>
            {notice.message}
            {notice.suggestion && (
              <span className="combo-notice-suggestion">{notice.suggestion}</span>
            )}
          </span>
        </p>
      )}

      {selected.some((id) => id.includes('protein')) && (
        <p className="caveat">{PROTEIN_CAVEAT}</p>
      )}

      {/* Rows already on screen stay; the failure is reported above them. */}
      {error !== null && rows.length > 0 && <Notice kind="error">{error}</Notice>}

      {loading && products.length === 0 ? (
        <div className="lb-list">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <div className="lb-skeleton" key={i} />
          ))}
        </div>
      ) : error !== null && rows.length === 0 ? (
        /*
         * A REQUEST THAT FAILED IS NOT AN EMPTY RESULT.
         *
         * Both used to render, with "Nothing to rank" underneath the error and
         * dominating it. When the combined ranking exceeded the Worker CPU
         * limit, the page reported that no product matched -- for a query with
         * ten thousand eligible products. The advice it offered, "try fewer
         * criteria", happened to work, which made a server fault look like a
         * search that was too narrow.
         *
         * These two states now exclude each other, and this one offers the
         * action that actually helps: try again.
         */
        <div className="empty-state">
          <h2>Could not load rankings</h2>
          <p>{error}</p>
          <button
            type="button"
            className="empty-retry"
            onClick={() => setRefreshKey((n) => n + 1)}
          >
            Try again
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <h2>Nothing to rank</h2>
          <p>
            {category === null
              ? 'No product has what these criteria need.'
              : `No product in ${category} has what these criteria need.`}
          </p>
        </div>
      ) : (
        <ol className="lb-list">
          {rows.map((row, i) => (
            <Fragment key={row.product.id}>
              {(i === 5 || (i > 5 && (i - 5) % 20 === 0)) && (
                <li className="lb-ad">
                  <AdSlot slot={`leaderboard-inline-${Math.floor((i - 5) / 20) + 1}`} />
                </li>
              )}
              <LeaderboardRow
              row={row}
              metric={metricForBoard(lead)}
              combinedScore={scoreFor(row.product.id)}
              criteriaMet={combinedFor(row.product.id)?.criteriaMet ?? null}
              criteriaTotal={combinedFor(row.product.id)?.criteriaTotal ?? null}
              isComparing={isComparing(row.product.id)}
              compareDisabled={compareFull}
              onToggleCompare={toggleCompare}
            />
            </Fragment>
          ))}
        </ol>
      )}

      {/*
        The scroll target that pulls in the next page. Kept outside the <ol> so
        it is never mistaken for a list item by a screen reader.
      */}
      {hasMore && <div ref={sentinel} aria-hidden="true" className="lb-sentinel" />}

      {loadingMore && (
        <div className="lb-list lb-more" aria-live="polite">
          {[0, 1, 2].map((i) => (
            <div className="lb-skeleton" key={i} />
          ))}
        </div>
      )}

      {/* A manual control as well: IntersectionObserver does not fire if the
          list is shorter than the viewport, and some browsers throttle it. */}
      {hasMore && !loadingMore && (
        <button type="button" className="lb-more-btn" onClick={() => setOffset((n) => n + PAGE_SIZE)}>
          Show more
        </button>
      )}

      {!loading && rows.length > 0 && (
        <p className="lb-count">
          {hasMore
            ? `${rows.length.toLocaleString()} of ${eligible.toLocaleString()}`
            : `All ${rows.length.toLocaleString()} shown`}
        </p>
      )}

      {meta?.isSampleData && (
        <p className="foot-note">Sample data — not live prices.</p>
      )}
    </>
  );
}

/**
 * The value each board displays.
 *
 * Every board is listed. There is deliberately no catch-all `default`: the old
 * one sent seven boards to price-per-100 g, so selecting "most protein" ranked
 * correctly by protein but showed a price beside it — and showed "N/A" for any
 * product not sold by weight, because price-per-100 g is undefined for those.
 * A missing entry here should be a type error, not a silently wrong number.
 */
const BOARD_METRIC: Record<string, Parameters<typeof LeaderboardRow>[0]['metric']> = {
  cheapest_per_100g: 'pricePer100g',
  cheapest_per_100ml: 'pricePer100ml',
  cheapest_outright: 'price',
  biggest_discount: 'price',
  largest_package: 'packageSize',
  protein_per_dollar: 'proteinPerDollar',
  cheapest_per_100kcal: 'pricePer100kcal',
  protein_per_100kcal: 'proteinPer100kcal',
  carbs_per_100kcal: 'carbsPer100kcal',
  fat_per_100kcal: 'fatPer100kcal',
  highest_protein: 'protein100',
  highest_carbs: 'carb100',
  highest_fat: 'fat100',
};

function metricForBoard(boardId: string): Parameters<typeof LeaderboardRow>[0]['metric'] {
  return BOARD_METRIC[boardId] ?? 'pricePer100g';
}

